import { ApolloError } from '@apollo/client'
import { isArray, isEmpty, isString } from 'lodash'

import i18n from 'modules/core/lib/i18n'
import { parseErrorData } from 'modules/core/lib/parseErrorData'
import app from 'modules/core/store/models/app'

import { BusinessError, NetworkError, ValidationError } from './errors'

export interface Return<T> {
  success: boolean
  value: T | ApplicationError
}

export interface SuccessfulReturn<T> extends Return<T> {
  success: true
  value: T
}

export function isSuccessful<T>(output: Return<T>): output is SuccessfulReturn<T> {
  return output.success
}

export interface Options {
  errorFlash?: boolean
  successFlash?: boolean
  successFlashTime?: number
  successMessage?: string
  errorMessage?: string
  loadingMessage?: string
}

export type ApplicationError = string | string[] | NetworkError | BusinessError | ValidationError

const fromErrorToFlash = (error: ApplicationError): FlashParams => {
  const shouldShowFlash = true
  let message = ''

  const genericMessage = i18n.t('errors.generic_error')

  if (isString(error) || isArray(error)) {
    message = parseErrorData(error)
  } else if (error instanceof BusinessError) {
    message = error.message
  } else if (error instanceof ValidationError) {
    message = parseErrorData(error.data)
  }

  return { message: isEmpty(message) ? genericMessage : message, shouldShowFlash }
}

interface FlashParams {
  message: string
  shouldShowFlash: boolean
}

const getMessageOrDefault = (
  errorMessage: string | undefined,
  error: ApplicationError
): FlashParams => {
  return errorMessage ? { message: errorMessage, shouldShowFlash: true } : fromErrorToFlash(error)
}

export default async function asyncAction<Output extends Promise<unknown>>(
  action: () => Output,
  {
    errorFlash = true,
    successFlash = true,
    successMessage,
    errorMessage,
    successFlashTime,
    loadingMessage,
  }: Options = {}
): Promise<Return<Awaited<Output>>> {
  let value
  try {
    if (loadingMessage) {
      app.setProgress({
        message: loadingMessage,
      })
    }

    value = await action()
    app.setProgress(null)
  } catch (error: unknown) {
    app.setProgress(null)

    if (
      !isString(error) &&
      !isArray(error) &&
      !(error instanceof NetworkError) &&
      !(error instanceof BusinessError) &&
      !(error instanceof ApolloError) &&
      !(error instanceof ValidationError)
    ) {
      throw error
    }

    // eslint-disable-next-line no-console
    console.error(error, JSON.stringify(error))

    if (errorFlash) {
      const { message, shouldShowFlash } = getMessageOrDefault(errorMessage, error)

      if (message && shouldShowFlash) {
        app.setFlash({
          type: 'wrong',
          message,
          time: successFlashTime,
        })
      }
      // NOTE: Decide what to do with network errors
    }

    return Promise.resolve({
      success: false,
      value: error,
    })
  }

  if (successFlash) {
    app.setFlash({
      type: 'correct',
      message: successMessage || i18n.t('common.saved_successfully'),
      time: successFlashTime,
    })
  }

  return Promise.resolve({
    success: true,
    value,
  })
}
